import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import type { ScanBucket } from './ScanChart.js'
import type { SoireeProjection, SoireeStats, SoireeView, TicketingFreshness } from './lib.js'
import type { AffichageBilletteries, VueNuit } from './nuit-billetteries-model.js'
import type { AssociationCourte, RattachementDeLaSoiree } from './rattachement-model.js'
import type { VenueFormules } from './formules-model.js'

/**
 * DONNÉES DE LA PAGE SOIRÉE — un contexte, plusieurs cycles de vie.
 *
 * Chaque famille de données vit à SA cadence (le cahier des charges
 * interdit de figer toutes les sources au même instant) :
 *   · détail (répartitions, courbe, timeline) — 30 s, la cadence du worker ;
 *   · projection opérationnelle (agents, staff, annonce…) — 15 s ;
 *   · comparaison — au changement de mode + 60 s.
 */

export interface SoireeDetailData {
  soiree: SoireeView & { timezone: string; stats: SoireeStats | null; stats_updated_at: string | null }
  products: { name: string; category: string | null; tickets_valid: number; tickets_scanned: number; scan_rate: number | null }[]
  channels: { channel: string; label: string; tickets_valid: number; tickets_scanned: number }[]
  scan_curve: { available: boolean; step_s: number; buckets: ScanBucket[] }
  timeline: { at: string; kind: string; label: string }[]
  freshness: TicketingFreshness
  /** La nuit de la soirée, toutes billetteries (absente d'un serveur d'avant le 16/10/2026). */
  night?: VueNuit | null
  /** Une soirée association : la soirée et ses rattachés, ce que la tuile Billetterie et ses tiroirs comptent (20/10/2026). */
  night_soiree?: VueNuit | null
  /** Les événements rattachés à cette soirée, ou la soirée du club de cet événement (19/10/2026). */
  rattachement?: RattachementDeLaSoiree | null
  /** Les billets des associations de la nuit, formule par formule (19/10/2026) ; `null` sans billet d'association. */
  formules?: VenueFormules | null
  billetteries?: AffichageBilletteries
  /**
   * Qui organise (20/10/2026) : l'association de chaque événement de la page
   * (la soirée, ses rattachés, la venue), et celles de la soirée entière.
   * `null` : pas le droit « Consulter les associations », ou réglage éteint.
   */
  associations?: { par_evenement: Record<string, AssociationCourte>; de_la_soiree: AssociationCourte[] } | null
}

export interface ComparisonData {
  comparison: {
    mode: string
    mode_label: string
    elapsed_s: number | null
    current: { scans: number | null; sales: number | null; total_scans: number | null; total_sales: number | null }
    reference: {
      id: string; name: string; starts_at: string; offset_days: number
      scans: number | null; sales: number | null
      total_scans: number | null; total_sales: number | null; sample_size?: number
    } | null
    /**
     * Ce que l'écart compare (revue du 19/10/2026) — la même règle que le
     * graphique : toute la nuit (défaut), Shotgun seul (réglage), ou la
     * soirée quand elle n'a personne avec elle. `phrase` le dit.
     */
    cible?: 'NUIT' | 'SHOTGUN' | 'SOIREE'
    phrase?: string | null
    /** Vrai avant l'ouverture : il n'y a rien à comparer, et surtout pas
     *  un « −100 % » qui ne mesurerait que le temps restant. */
    not_started: boolean
    delta_scans_pct: number | null
    delta_sales_pct: number | null
  }
  benchmarks: {
    family: string; clean_history: boolean; events_count: number
    record_valid: number | null; record_scanned: number | null
    record_event: { id: string; name: string; starts_at: string } | null
    avg_valid: number | null
  } | null
}

interface Ctx {
  id: string
  detail: SoireeDetailData | null
  projection: SoireeProjection | null
  compareMode: string
  setCompareMode: (m: string) => void
  comparison: ComparisonData | null
  reloadProjection: () => void
  /** Relire le détail tout de suite (après un rattachement, un « Défaire »). */
  reloadDetail: () => void
  openDrill: (drill: string) => void
  drill: string | null
  closeDrill: () => void
}

const SoireeCtx = createContext<Ctx | null>(null)

export function useSoiree(): Ctx {
  const ctx = useContext(SoireeCtx)
  if (!ctx) throw new Error('useSoiree hors SoireeDataProvider')
  return ctx
}

async function fetchJson<T>(url: string): Promise<T | null> {
  try {
    const r = await fetch(url, { credentials: 'same-origin' })
    return r.ok ? (await r.json()) as T : null
  } catch { return null }
}

export function SoireeDataProvider({ id, children, onNotFound }: {
  id: string
  children: ReactNode
  onNotFound: () => void
}) {
  const [detail, setDetail] = useState<SoireeDetailData | null>(null)
  const [projection, setProjection] = useState<SoireeProjection | null>(null)
  const [compareMode, setCompareMode] = useState('SAME_WEEKDAY_N1')
  const [comparison, setComparison] = useState<ComparisonData | null>(null)
  const [drill, setDrill] = useState<string | null>(null)
  const [projTick, setProjTick] = useState(0)
  const [detailTick, setDetailTick] = useState(0)

  useEffect(() => {
    let vivant = true
    const charger = () => {
      fetch(`/api/v1/soirees/${id}`, { credentials: 'same-origin' })
        .then((r) => {
          if (r.status === 404) { onNotFound(); return Promise.reject(new Error('404')) }
          return r.ok ? r.json() : Promise.reject(new Error(String(r.status)))
        })
        .then((j: SoireeDetailData) => { if (vivant) setDetail(j) })
        .catch(() => undefined)
    }
    charger()
    const t = setInterval(charger, 30_000)
    return () => { vivant = false; clearInterval(t) }
  }, [id, detailTick]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let vivant = true
    const charger = () => {
      void fetchJson<SoireeProjection>(`/api/v1/soirees/${id}/projection`)
        .then((j) => { if (vivant && j) setProjection(j) })
    }
    charger()
    const t = setInterval(charger, 15_000)
    return () => { vivant = false; clearInterval(t) }
  }, [id, projTick])

  useEffect(() => {
    let vivant = true
    const charger = () => {
      void fetchJson<ComparisonData>(`/api/v1/soirees/${id}/compare?mode=${compareMode}`)
        .then((j) => { if (vivant && j) setComparison(j) })
    }
    charger()
    const t = setInterval(charger, 60_000)
    return () => { vivant = false; clearInterval(t) }
  }, [id, compareMode])

  return (
    <SoireeCtx.Provider value={{
      id, detail, projection, compareMode, setCompareMode, comparison,
      reloadProjection: () => setProjTick((x) => x + 1),
      reloadDetail: () => setDetailTick((x) => x + 1),
      openDrill: setDrill, drill, closeDrill: () => setDrill(null),
    }}>
      {children}
    </SoireeCtx.Provider>
  )
}
